import { fmtTime } from '../../lib/format'
import { Children, Fragment, isValidElement, type ReactNode } from 'react'
import { hostPower, powerSource } from '../../lib/power'

// Chips encode state in form: one look per meaning, the same everywhere.
// Status colours are separate from the brand: lime never means "ok"; every
// chip carries a word, never colour alone.
export type Tone = 'neutral' | 'outline' | 'ok' | 'warn' | 'crit' | 'info' | 'level'

const tones: Record<Tone, string> = {
  neutral: 'bg-[#EDF1F4] text-[#3D5260] border-transparent',
  outline: 'bg-transparent text-muted-foreground border-border',
  ok: 'bg-[#E2F3E9] text-[#146C43] border-transparent',
  warn: 'bg-[#FFF1D6] text-[#8A4B00] border-transparent',
  crit: 'bg-[#FCE4E1] text-[#A3271D] border-transparent',
  info: 'bg-secondary text-primary border-transparent',
  level: 'bg-ink text-white border-transparent',
}

// flat lists the nodes to show, fragments unpacked (empty ones count as nothing).
function flat(n: ReactNode): ReactNode[] {
  return Children.toArray(n).flatMap((c) => (isValidElement(c) && c.type === Fragment ? flat((c.props as { children?: ReactNode }).children) : [c]))
}

// CellStack is the one layout of a table cell with chips: the chips together
// on top, the plain facts (power, notes, versions) below - never a chip under
// a line of text.
export function CellStack({ chips, children }: { chips?: ReactNode; children?: ReactNode }) {
  const top = flat(chips)
  const below = flat(children)
  return (
    <div className="flex flex-col items-start gap-1">
      {top.length > 0 && <div className="flex flex-col items-start gap-1">{top}</div>}
      {below}
    </div>
  )
}

export function Chip({ tone = 'neutral', title, children, className = '' }: { tone?: Tone; title?: string; children: ReactNode; className?: string }) {
  return (
    <span title={title} className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-bold leading-[18px] ${tones[tone]} ${className}`}>
      {children}
    </span>
  )
}

// Lifecycle (derived by the controller, docs/MACHINES.md): one word and tone per stage
const lifecycleTone: Record<string, Tone> = {
  discovered: 'neutral',
  identified: 'warn',
  inspected: 'neutral',
  ready: 'ok',
  reserved: 'outline',
  installing: 'warn',
  maintenance: 'warn',
  'in-use': 'info',
  error: 'crit',
}

export function Lifecycle({ value }: { value?: string }) {
  if (!value) return null
  return <Chip tone={lifecycleTone[value] || 'neutral'}>{value === 'in-use' ? 'in use' : value}</Chip>
}

// Power: the host's power as its BMC or its own agent last reported it
export function Power({ state, at, source }: { state?: string; at?: string; source?: 'bmc' | 'agent' }) {
  const m = { power_state: state, power_at: at, power_source: source }
  const s = hostPower(m)
  const on = s === 'on'
  const title = at ? `${powerSource(m)} · ${fmtTime(at)}` : 'not reported yet'
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs" title={title}>
      <span aria-hidden className={`inline-block h-2 w-2 rounded-full ${on ? 'bg-[#1E8A55]' : s === 'off' ? 'bg-[#8FA5B3]' : 'border border-[#8FA5B3]'}`} />
      {s || 'unknown'}
    </span>
  )
}

// Machine lifecycle: discovered -> ready -> provisioning -> active -> retired, error
const stateTone: Record<string, Tone> = {
  discovered: 'neutral',
  ready: 'outline',
  provisioning: 'warn',
  active: 'ok',
  error: 'crit',
  retired: 'neutral',
}

export function MachineState({ state }: { state: string }) {
  return <Chip tone={stateTone[state] || 'neutral'}>{state}</Chip>
}

// Network planes of a cluster
const planeLabel: Record<string, string> = {
  'oob-bmc': 'OOB-BMC',
  'oob-switch': 'OOB-SW',
  'oob-pdu': 'OOB-PDU',
  mgmt: 'Mgmt',
  'mgmt-data': 'Mgmt-Data',
  data: 'Data',
  transfer: 'Transfer',
  routed: 'Routed',
  interlink: 'Interlink',
  storage: 'Storage',
}

export function PlaneChip({ plane, children }: { plane: string; children?: ReactNode }) {
  return (
    <Chip tone="outline" className="font-mono">
      <span className="text-foreground/80">{planeLabel[plane] || plane}</span>
      {children}
    </Chip>
  )
}

export function Dot({ on, title }: { on: boolean; title?: string }) {
  return <span title={title} className={`inline-block h-2 w-2 shrink-0 rounded-full ${on ? 'bg-green-500' : 'bg-red-500'}`} />
}

// CPUChip shows the CPU generation; the tone says how current it is
// (e.g. EPYC Naples old, Rome mid, Milan and newer current)
export function CPUChip({ gen }: { gen?: { short: string; name?: string; arch?: string; tier?: 'old' | 'mid' | 'current' } }) {
  if (!gen?.name) return null
  const tone: Tone = gen.tier === 'old' ? 'warn' : gen.tier === 'current' ? 'ok' : 'neutral'
  return <Chip tone={tone} title={`${gen.short}: ${gen.name}${gen.arch ? ` (${gen.arch})` : ''}`}>{gen.name}{gen.arch && gen.arch.startsWith('Zen') ? ` · ${gen.arch}` : ''}</Chip>
}
